import React, { useEffect, useRef } from "react";

function clamp01(value) {
  return Math.max(0, Math.min(1, Number(value) || 0));
}

/**
 * Gemini-style fluid waveform: overlapping sines driven by mic level.
 * Level comes from useVoiceToText's speech-band analyser (not raw EQ bars).
 */
export default function VoiceFrequencyBars({ level = 0, active }) {
  const canvasRef = useRef(null);
  const levelRef = useRef(0);
  const rafRef = useRef(null);
  const smoothedRef = useRef(0);

  levelRef.current = clamp01(level * 1.75);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth || window.innerWidth;
      const h = canvas.clientHeight || 48;
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener("resize", resize);

    const drawWave = (amp, color, scaleY, speed, phase, cssW, cssH) => {
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      const time = performance.now() * speed;
      const centerY = cssH / 2;
      const maxH = 18 * amp;
      for (let x = 0; x <= cssW; x += 2) {
        const y = centerY + Math.sin(x * 0.03 + time + phase) * maxH * scaleY;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    };

    const tick = () => {
      const cssW = canvas.clientWidth || window.innerWidth;
      const cssH = canvas.clientHeight || 48;
      const target = active ? levelRef.current : 0;
      const idle = active ? 0.08 : 0;
      smoothedRef.current = smoothedRef.current * 0.82 + (target + idle) * 0.18;
      const amp = smoothedRef.current;

      ctx.clearRect(0, 0, cssW, cssH);
      if (active || amp > 0.01) {
        drawWave(amp, "#00ffc8", 1, 0.002, 0, cssW, cssH);
        drawWave(amp, "rgba(0, 150, 255, 0.55)", 0.7, 0.004, Math.PI / 2, cssW, cssH);
        drawWave(amp, "rgba(150, 0, 255, 0.35)", 0.45, 0.001, Math.PI, cssW, cssH);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("resize", resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active]);

  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: "calc(env(safe-area-inset-bottom) + 86px)",
        zIndex: 1550,
        pointerEvents: "none",
        width: "100vw",
        height: active ? "48px" : "0px",
        opacity: active ? 0.92 : 0,
        transition: "opacity 0.22s ease, height 0.22s ease",
        overflow: "hidden",
        filter: active ? "drop-shadow(0 0 8px rgba(0,255,200,0.28))" : "none",
      }}
      aria-label={active ? "La app está registrando la voz" : "Monitor de voz inactivo"}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(clamp01(level) * 100)}
    >
      <canvas
        ref={canvasRef}
        style={{ display: "block", width: "100%", height: "100%", background: "transparent" }}
      />
    </div>
  );
}
